/**
 * Modal for raising an inactive-insurance style query and persisting CIG box write-back notes.
 * @module raiseQuery
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { updateCigBoxNoteNewEndpoint } from "../../../api/postCall/updateCigBoxNote";
import {
  DEFAULT_INSURANCE_ISSUE_TEXT,
  RAISE_QUERY_MODAL_CANCEL_BUTTON_TEXT,
  RAISE_QUERY_MODAL_RADIO_BUTTON_TEXT,
  RAISE_QUERY_MODAL_SAVE_BUTTON_TEXT,
  RAISE_QUERY_MODAL_TITLE,
} from "../../../constants/stringLiterals";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../../../data-model/busniessOfficeNote";
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { BusniessOfficeReportType } from "../../../enums/busniessOfficeReportType";
import { ModalId } from "../../../enums/modalId";
import { useHealthCareFacility } from "../../../hooks/useHealthCareFacility";
import { generateBusniessOfficeReportForInactiveInsurance } from "../../../pages/coverageInfo/helpers/generateBusniessOfficeReportForInactiveInsurance";
import { closeModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { insertCommentWritebackOncoemrTrailComment } from "../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { checkIfStringIsEmpty } from "../../../utils/stringModifications";
import { validateReportStatusesAgainstBoOptions } from "../../../utils/validateBoStatus";
import BadgeWithIcon from "../../badgeWithIcon/badgeWithIcon";
import { ToastMessages } from "../../toasts/toastMessages";

/** Props for the raise-query (insurance issue) modal. */
interface RaiseQueryModalProps {
  onCommentsChange: (comment: string) => void;
  queryType?: string;
  reports?: BusniessOfficeNote[];
  /** Unwrapped general order details used to build the write-back payload. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Optional callback fired after a successful save so the parent can refetch. */
  onSaveSuccess?: () => void | Promise<void>;
}

/**
 * Builds BO write-back payloads from optional reports, validates BO options, and calls {@link updateCigBoxNoteNewEndpoint}.
 *
 * @param props - Comment change callback, optional default query type, and source reports.
 */
const RaiseQueryModal = (props: RaiseQueryModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [queryType, setQueryType] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [writeBackReports, setWriteBackReports] = useState<AllCommentsProps>({
    comments: [],
    commentDate: "",
    type: BusniessOfficeReportType.FINAL,
  });
  const { generalOrderDetails } = props;
  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { healthCareFacilityData } = useHealthCareFacility(
    getOrgIdForFetchExternalWorklist() ?? "",
  );
  const { fetchAll } = useDataAccumulatorForSingleOrder();
  useEffect(() => {
    if (props.reports && props.reports.length > 0) {
      const postReports = generateBusniessOfficeReportForInactiveInsurance(
        props.reports,
      );
      if (postReports) {
        setWriteBackReports(postReports);
      }
    }
  }, [props.reports]);
  useEffect(() => {
    setQueryType(props.queryType || "");
  }, [props.queryType]);

  const getInsuranceIssueTextForRadioButton = useMemo(() => {
    return !checkIfStringIsEmpty(
      healthCareFacilityData?.data?.inactive_insurance_bo_status,
    )
      ? healthCareFacilityData?.data?.inactive_insurance_bo_status || ""
      : DEFAULT_INSURANCE_ISSUE_TEXT;
  }, [healthCareFacilityData.data]);

  const handleSave = async () => {
    setLoading(true);
    const notes = writeBackReports.comments.map((comment) => {
      return {
        note: queryType,
        report_status: getInsuranceIssueTextForRadioButton,
        report_identifier: comment.id,
      };
    });

    const payload = {
      patient_id: generalOrderDetails?.payload?.patient_id ?? "",
      regimen_name: generalOrderDetails?.payload?.regimen_name ?? "",
      order_id: generalOrderDetails?.order_id ?? "",
      date_of_service: generalOrderDetails?.payload?.date_of_service ?? "",
      write_back_reports: notes,
      org_id: getOrgIdForFetchExternalWorklist(),
      bo_status: getInsuranceIssueTextForRadioButton,
    };

    try {
      // Validate that bo_status and all report_status values exist in bo_options
      const reportStatuses = payload.write_back_reports.map(
        (report) => report.report_status,
      );
      const statusesToValidate = [payload.bo_status, ...reportStatuses].filter(
        (status) => status,
      );

      if (statusesToValidate.length > 0) {
        const validationResult = validateReportStatusesAgainstBoOptions(
          boOptions,
          statusesToValidate,
        );

        if (!validationResult.isValid) {
          toast.error(
            `Invalid BO status values: ${validationResult.invalidStatuses.join(", ")}`,
          );
          setLoading(false);
          return;
        }
      }

      await updateCigBoxNoteNewEndpoint(payload);

      if (notes.length > 0) {
        const actingProviderName =
          `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
          "Unknown Provider";

        insertCommentWritebackOncoemrTrailComment({
          orderId: generalOrderDetails?.order_id ?? "",
          orgId: getOrgIdForFetchExternalWorklist() || "",
          patientId: generalOrderDetails?.payload?.patient_id ?? "",
          providerEmail: user?.email ?? "",
          providerName: actingProviderName,
          fromBoStatus: generalOrderDetails?.status?.bo_status,
          toBoStatus: payload.bo_status,
          boOptions: boOptions ?? [],
        });
      }

      const orderId = generalOrderDetails?.order_id ?? "";
      const orgId = getOrgIdForFetchExternalWorklist();
      if (orderId && orgId) {
        await fetchAll(orderId, orgId);
      }
      toast.success(ToastMessages.SIG_BOX_NOTE_UPDATED_SUCCESS);
      dispatch(closeModal());
    } catch (error) {
      console.error(error);
      toast.error(ToastMessages.SIG_BOX_NOTE_UPDATED_FAILURE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      dialogId={ModalId.INSURANCE_ERROR_DIALOG}
      onSave={handleSave}
      onCancel={() => {
        dispatch(closeModal());
      }}
      onClose={() => {
        dispatch(closeModal());
      }}
      title={RAISE_QUERY_MODAL_TITLE}
      saveButtonText={RAISE_QUERY_MODAL_SAVE_BUTTON_TEXT}
      disableSave={loading}
      cancelText={RAISE_QUERY_MODAL_CANCEL_BUTTON_TEXT}
    >
      <div className="raise-query__container rounded-lg border border-primaryGray-14">
        <div className="raise-query__radio-btn flex items-center border-b border-primaryGray-14 p-2 text-h12">
          {RAISE_QUERY_MODAL_RADIO_BUTTON_TEXT}
          {healthCareFacilityData?.loading ? (
            <>
              <SpinningLoader />
            </>
          ) : (
            <>
              <div className="status ml-3">
                <BadgeWithIcon
                  text={getInsuranceIssueTextForRadioButton}
                  id={getInsuranceIssueTextForRadioButton}
                  textColor="#0F0F0F"
                  bgColor="#E5E5E5"
                />
              </div>
            </>
          )}
        </div>
        <div className="raise-query__comments p-2">
          <textarea
            className="h-48 w-full rounded-lg border border-none border-primaryGray-14 text-h12 outline-none"
            value={queryType}
            onChange={(e) => {
              setQueryType(e.target.value);
              props.onCommentsChange(e.target.value);
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RaiseQueryModal;
